import { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Quote,
  Compass,
  Network,
  GitBranch,
  Users,
  Layers,
  Sprout,
  Anchor,
} from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import heroBlackMarble from '@/assets/hero-for-organizations.jpg';
import coEvolutionDiagram from '@/assets/co-evolution-diagram.jpg';
import coherenceImage from '@/assets/orgs-coherence-currents.jpg';
import horizonImage from '@/assets/leaders-threshold-horizon.jpg';

const intentions = [
  {
    n: '01',
    icon: Compass,
    eyebrow: 'Vertical Development',
    title: 'Of individual leaders.',
    body:
      'Growing the interior capacity to hold complexity, ambiguity, and paradox.',
  },
  {
    n: '02',
    icon: Network,
    eyebrow: 'Relational Field Development',
    title: 'Between practitioners and the clients they serve.',
    body:
      'Building shared language and shared orientation across the people doing the work together. The engagement itself becomes the developmental container.',
  },
  {
    n: '03',
    icon: GitBranch,
    eyebrow: 'Human and AI Threshold',
    title: 'Navigated with discernment and presence.',
    body:
      'Developing the judgment to know when to trust the algorithm and when to trust the human in the room.',
  },
];

const principles = [
  {
    n: '01',
    icon: Anchor,
    title: 'Senior leaders participate alongside their teams.',
    body:
      'A developmental culture has to be modeled from the top. When senior leaders do the same interior work as their teams, the program earns its depth.',
  },
  {
    n: '02',
    icon: Users,
    title: 'Practitioners and clients develop together.',
    body:
      'Where possible, cohorts include both delivery leaders and their client counterparts. The field between them is the program.',
  },
  {
    n: '03',
    icon: Sprout,
    title: 'Scale through coherent early adopters.',
    body:
      'This work scales through a small group of early adopters who embody the practice and carry it forward as co-designers.',
  },
  {
    n: '04',
    icon: Layers,
    title: 'Depth over breadth.',
    body:
      'We design for depth. Programs that shift how leaders develop and relate produce durable value over years, not weeks.',
  },
];

// ============ Hero ============
const OrgsHero = () => {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="relative min-h-[65vh] sm:min-h-[85vh] flex items-end overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat will-change-transform animate-[heroZoom_24s_ease-out_forwards]"
        style={{
          backgroundImage: `url(${heroBlackMarble})`,
          transform: `translate3d(0, ${scrollY * 0.12}px, 0)`,
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-foreground/75" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/60 to-foreground/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground/70 via-transparent to-transparent" />

      <div className="container-mobile relative z-10 pb-16 sm:pb-28 pt-24 sm:pt-40 max-w-7xl mx-auto w-full">
        <div className="max-w-3xl">
          <div className="flex items-center gap-4 mb-8 animate-fade-in">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              For Organizations
            </p>
          </div>

          <h1
            className="text-background text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-8xl mb-8 sm:mb-10 leading-[1] sm:leading-[0.95] animate-fade-in"
            style={{ textWrap: 'balance', animationDelay: '120ms', animationFillMode: 'backwards' }}
          >
            Developing leadership capacity for the{' '}
            <span className="text-primary italic font-display">age of AI.</span>
          </h1>

          <p
            className="text-background/85 text-lg sm:text-2xl leading-relaxed max-w-2xl font-normal animate-fade-in"
            style={{ animationDelay: '240ms', animationFillMode: 'backwards' }}
          >
            Building the conditions and capacities that carry leaders and the people they serve.
          </p>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-background/30 to-transparent z-10" />

      <style>{`
        @keyframes heroZoom {
          from { transform: scale(1.08); }
          to { transform: scale(1); }
        }
      `}</style>
    </section>
  );
};

// ============ The Moment We're In ============
const TheMoment = () => {
  const eyebrow = useScrollReveal<HTMLDivElement>();
  const heading = useScrollReveal<HTMLHeadingElement>();
  const body = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-background via-secondary/40 to-background py-32 sm:py-44">
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, hsl(var(--primary) / 0.06) 0%, transparent 55%), radial-gradient(ellipse at 70% 80%, hsl(var(--sage) / 0.05) 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-5xl mx-auto">
        <div
          ref={eyebrow.ref}
          className={`flex items-center gap-4 mb-10 transition-all duration-700 ${
            eyebrow.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="h-px w-12 bg-primary" aria-hidden="true" />
          <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
            The Moment We're In
          </p>
        </div>

        <h2
          ref={heading.ref}
          className={`text-foreground text-4xl sm:text-5xl lg:text-6xl mb-16 max-w-4xl leading-[1.05] transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ textWrap: 'balance' }}
        >
          AI is collapsing the boundary between technical delivery, strategic counsel, and{' '}
          <span className="text-primary italic">human relationship.</span>
        </h2>

        <div
          ref={body.ref}
          className={`max-w-3xl space-y-8 text-foreground/85 text-lg sm:text-xl leading-relaxed font-normal transition-all duration-1000 delay-200 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p>
            When AI handles the deterministic work, what remains is human. Judgment. Trust. The capacity to stay present when the situation is unresolved.
          </p>
          <blockquote className="border-l-2 border-primary pl-6 py-2 font-display italic text-foreground text-2xl sm:text-3xl leading-snug">
            The hardest part of AI deployment is the human part. That is the work.
          </blockquote>
          <p>
            This calls for a different kind of leadership investment.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============ The Core Insight - dark editorial spread with diagram ============
const CoreInsight = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const figure = useScrollReveal<HTMLElement>();
  const body = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-20 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              The Core Insight
            </p>
          </div>
          <h2
            className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-8"
            style={{ textWrap: 'balance' }}
          >
            The relational field is the{' '}
            <span className="text-primary italic">generative ground.</span>
          </h2>
          <p className="text-background/75 text-xl leading-relaxed font-normal max-w-2xl">
            This work focuses on what gets created between people.
          </p>
        </div>

        <figure
          ref={figure.ref}
          className={`mb-20 transition-all duration-1000 ${
            figure.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="rounded-sm overflow-hidden border border-background/10 shadow-2xl bg-background/[0.02]">
            <img
              src={coEvolutionDiagram}
              alt="Co-evolution of human and machine intelligence: AI Infrastructure and Human Intelligence converging in Co-Created Capabilities, with a tension and risk zone below."
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
        </figure>

        <div
          ref={body.ref}
          className={`max-w-3xl mx-auto space-y-8 text-lg sm:text-xl text-background/80 leading-relaxed font-normal transition-all duration-1000 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p>
            The relational field between a practitioner and their client is where the real value lives. It is the ground from which outcomes neither party could design alone emerge.
          </p>
          <p className="text-background/70 font-display italic text-xl sm:text-2xl leading-snug border-l-2 border-primary/60 pl-6 py-2">
            What we could not find alone becomes possible together.
          </p>
        </div>

        {/* Section punchline - elevated, full-bleed editorial */}
        <div className="max-w-5xl mx-auto mt-20 sm:mt-24">
          <div className="flex items-center gap-4 mb-8 justify-center">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              The Edge
            </p>
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
          </div>
          <p
            className="text-background font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-center"
            style={{ textWrap: 'balance' }}
          >
            This is the developmental edge that AI is calling leaders to{' '}
            <span className="text-primary italic">grow into.</span>
          </p>
          <p
            className="text-background/75 text-xl sm:text-2xl leading-relaxed font-normal text-center mt-8 max-w-3xl mx-auto"
            style={{ textWrap: 'balance' }}
          >
            Greater capacity in relationship, where human and machine intelligence meet.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============ Three Intentions - cinematic dark cards ============
const IntentionCard = ({
  item,
  index,
}: {
  item: typeof intentions[number];
  index: number;
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const Icon = item.icon;
  return (
    <article
      ref={ref}
      style={{ transitionDelay: visible ? `${index * 120}ms` : '0ms' }}
      className={`group relative overflow-hidden rounded-sm p-8 sm:p-10 transition-all duration-700
        bg-card
        border border-border
        shadow-sm
        hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      {/* Hover-revealed terracotta wash */}
      <div
        className="absolute top-0 right-0 w-40 h-40 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at top right, hsl(var(--primary) / 0.20) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      {/* (Number watermark removed) */}

      <div className="relative flex items-center gap-4 mb-7">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors duration-500">
          <Icon
            className="w-6 h-6 text-primary group-hover:scale-110 transition-transform duration-500"
            aria-hidden="true"
          />
        </div>
        <span className="h-px flex-1 bg-border group-hover:bg-primary/40 transition-colors duration-500" />
      </div>

      <p className="relative text-primary font-semibold tracking-[0.2em] uppercase text-xs mb-4">
        {item.eyebrow}
      </p>
      <h3 className="relative text-foreground font-display text-2xl sm:text-[1.65rem] mb-5 leading-tight group-hover:text-primary transition-colors duration-500">
        {item.title}
      </h3>
      <p className="relative text-foreground leading-relaxed text-base sm:text-lg font-normal">
        {item.body}
      </p>
    </article>
  );
};

const ThreeIntentions = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      {/* Layered painterly washes */}
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.18) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-20 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              What We Develop
            </p>
          </div>
          <h2
            className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Three interconnected <span className="text-primary italic">intentions.</span>
          </h2>
        </div>
        <div className="grid gap-6 md:gap-8 md:grid-cols-3">
          {intentions.map((item, i) => (
            <IntentionCard key={item.n} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Islands of Coherence - cinematic ============
const IslandsOfCoherence = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const image = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative bg-secondary/40 text-foreground py-32 sm:py-44 overflow-hidden">
      {/* Painterly cream wash for variation between two dark sections */}
      <div
        className="absolute inset-0 opacity-70 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 20% 15%, hsl(var(--primary) / 0.08) 0%, transparent 55%), radial-gradient(ellipse at 85% 85%, hsl(var(--sage) / 0.08) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-20 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              A Systems Perspective
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Islands of <span className="text-primary italic">coherence.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Painterly currents image */}
          <div
            ref={image.ref}
            className={`md:col-span-5 md:sticky md:top-32 transition-all duration-1000 ${
              image.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-2xl">
              <img
                src={coherenceImage}
                alt=""
                width={1024}
                height={1280}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to bottom, transparent 60%, hsl(var(--foreground) / 0.4) 100%)',
                }}
              />
            </div>

            <figure className="mt-8">
              <Quote className="h-6 w-6 text-primary mb-3" />
              <blockquote className="text-foreground/85 text-lg sm:text-xl leading-snug font-display italic">
                When a complex system is far from equilibrium, small islands of coherence in a sea of chaos have the capacity to shift the entire system to a higher order.
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-3">
                <div className="w-8 h-px bg-primary" />
                <p className="text-primary text-sm font-semibold">
                  Ilya Prigogine
                  <span className="text-foreground/85 font-normal"> / Nobel Prize-winning chemist</span>
                </p>
              </figcaption>
            </figure>
          </div>

          <div
            ref={body.ref}
            className={`md:col-span-7 space-y-8 text-lg sm:text-xl text-foreground/90 leading-relaxed font-normal transition-all duration-1000 delay-200 ${
              body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p>
              Organizations reinventing themselves in the wake of AI are inside a genuine far-from-equilibrium condition. Roles collapsing, delivery models restructuring, the practitioner and client relationship itself in question. The system is destabilized, which means it is also open.
            </p>
            <p>
              In a far-from-equilibrium system, transformation comes from small groups developing a shared orientation different from the surrounding noise, then carrying it back into the systems they are already embedded in.
            </p>
            <p>
              We enroll leaders whose sphere of influence exceeds their span of control, developing a shared orientation together. Because the system is far from equilibrium, that coherence propagates. The surrounding system reorganizes around people already trusted and in the room, now seeing and operating differently.
            </p>
            <p className="text-foreground font-display italic text-2xl sm:text-3xl leading-snug border-l-2 border-primary pl-6 py-2">
              A genuinely coherent container, with the right people, developed to depth, produces a phase transition.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ Design Principles - dark cinematic spread ============
const PrincipleCard = ({
  item,
  index,
}: {
  item: typeof principles[number];
  index: number;
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const Icon = item.icon;
  return (
    <article
      ref={ref}
      style={{ transitionDelay: visible ? `${(index % 2) * 120}ms` : '0ms' }}
      className={`group relative overflow-hidden rounded-sm p-8 sm:p-10 transition-all duration-700
        bg-background/[0.04] backdrop-blur-sm
        border border-background/15
        hover:border-primary/60 hover:bg-background/[0.06] hover:-translate-y-1
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div
        className="absolute top-0 right-0 w-40 h-40 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at top right, hsl(var(--primary) / 0.18) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      {/* (Number watermark removed) */}

      <div className="relative flex items-center gap-4 mb-7">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/15 group-hover:bg-primary/25 transition-colors duration-500">
          <Icon
            className="w-6 h-6 text-primary group-hover:scale-110 transition-transform duration-500"
            aria-hidden="true"
          />
        </div>
        <span className="h-px flex-1 bg-background/20 group-hover:bg-primary/40 transition-colors duration-500" />
      </div>
      <h3 className="relative text-background font-display text-2xl sm:text-[1.65rem] mb-4 leading-tight group-hover:text-primary transition-colors duration-500">
        {item.title}
      </h3>
      <p className="relative text-background/75 leading-relaxed text-base sm:text-lg font-normal">
        {item.body}
      </p>
    </article>
  );
};

const DesignPrinciples = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.15) 0%, transparent 55%), radial-gradient(ellipse at 20% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-20 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Design Principles
            </p>
          </div>
          <h2
            className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            The conditions that make this <span className="text-primary italic">work, work.</span>
          </h2>
        </div>
        <div className="grid gap-6 md:gap-8 md:grid-cols-2">
          {principles.map((item, i) => (
            <PrincipleCard key={item.n} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ CTA Threshold ============
const OrgsThreshold = () => {
  const handleScheduleCall = () => {
    window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
  };
  const content = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative min-h-[70vh] sm:min-h-[80vh] flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${horizonImage})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-foreground/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-foreground/50 via-foreground/40 to-foreground/85" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, hsl(var(--foreground) / 0.55) 0%, transparent 65%)',
        }}
        aria-hidden="true"
      />

      <div
        ref={content.ref}
        className={`container-mobile relative z-10 max-w-4xl mx-auto py-24 sm:py-40 text-center transition-all duration-1000 ${
          content.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h2
          className="text-background text-[2.75rem] sm:text-6xl lg:text-7xl mb-8 sm:mb-10 leading-[1.0]"
          style={{
            textWrap: 'balance',
            textShadow: '0 2px 24px hsl(var(--foreground) / 0.9), 0 0 8px hsl(var(--foreground) / 0.7)',
          }}
        >
          Human capacity{' '}
          <span
            className="italic font-display"
            style={{
              color: '#F5A572',
              textShadow:
                '0 2px 18px hsl(var(--foreground) / 0.95), 0 0 10px hsl(var(--foreground) / 0.85)',
            }}
          >
            at scale.
          </span>
        </h2>

        <p className="text-background/90 text-lg sm:text-2xl leading-relaxed mb-10 sm:mb-14 max-w-2xl mx-auto font-normal drop-shadow">
          The asset that does not commoditize.
        </p>

        <Button
          onClick={handleScheduleCall}
          size="lg"
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-2xl hover:shadow-primary/30 hover:scale-105 group"
        >
          Begin the Conversation
          <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </section>
  );
};

// ============ Page ============
const ForOrganizations = () => {
  const orgsJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Leadership Capacity for the Age of AI',
      provider: { '@type': 'Organization', name: 'ParadoxEdge', url: 'https://www.paradoxedge.com' },
      serviceType: ['Leadership Development', 'Organizational Development', 'Executive Coaching', 'Vertical Development'],
      description:
        'Creating and sustaining cultures and systems of continuous practice and ongoing development for organizations navigating the human dimensions of AI.',
      url: 'https://www.paradoxedge.com/for-organizations',
      areaServed: 'Worldwide',
      audience: { '@type': 'BusinessAudience', audienceType: 'Enterprise leadership teams and consulting practices' }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.paradoxedge.com/' },
        { '@type': 'ListItem', position: 2, name: 'For Organizations', item: 'https://www.paradoxedge.com/for-organizations' }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What does ParadoxEdge do for organizations?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We build cultures and systems of continuous practice for organizations navigating the human dimensions of AI. Programs grow vertical development in individual leaders, relational depth across teams, and discernment at the human–AI threshold.'
          }
        },
        {
          '@type': 'Question',
          name: 'What is the Second Loop?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Second Loop is the developmental loop every organization needs to run alongside its AI "token capital" loop. Tokens compound what your people knew; the Second Loop compounds the judgment, taste, and trust they will need tomorrow.'
          }
        },
        {
          '@type': 'Question',
          name: 'How is this different from traditional leadership training?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Traditional training transfers content. This work grows capacity: the interior capability to hold complexity, ambiguity, and paradox. It is designed for depth and compounds over years, not weeks.'
          }
        },
        {
          '@type': 'Question',
          name: 'Who participates?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Senior leaders participate alongside their teams. Where possible, cohorts include both delivery leaders and their client counterparts. The relational field between them is the program.'
          }
        },
        {
          '@type': 'Question',
          name: 'How do engagements begin?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'With a first conversation to see whether your organization is ready to seed an island of coherence. Schedule at https://calendly.com/kentfrazier/30-minutes.'
          }
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="For Organizations | Leadership Capacity for the Age of AI"
        description="Cultures and systems of continuous practice for organizations navigating the human dimensions of AI. Vertical development, relational fields, the human–AI threshold."
        canonicalUrl="/for-organizations"
        keywords="leadership development AI, organizational development, vertical development, executive coaching, AI transformation leadership, far-from-equilibrium"
        jsonLd={orgsJsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <ElfsightWidget />

        <main>
          <OrgsHero />
          <TheMoment />
          <CoreInsight />
          <ThreeIntentions />
          <IslandsOfCoherence />
          <DesignPrinciples />
          <OrgsThreshold />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ForOrganizations;
